// Werbeplanung: welcher Kunde mit welchem Spot wann in die Werbeblöcke darf.
//
// Die Buchungen selbst stehen in traffic_slots (API: /channels/:slug/traffic-slots,
// /traffic-slots/:id). Auf Sendung kommen sie nur über einen Platz „Werbeblock“
// in einer Stundenuhr – die Seite zeigt deshalb auch, ob der Kanal so eine Uhr hat.
// Die Rechenregeln (läuft/geplant/abgelaufen, erlaubte Stunden) stehen in ../werbung.ts.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { can } from "@webradiosuite/shared";
import { api, werbungApi } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { Icon, errorText } from "../components/streaming-ui";
import { Badge, Button, Callout, Card, EmptyState, Page, PageHeader, Skeleton, StatTile } from "../components/ui";
import type { Channel, LibraryItem, TrafficSlotEingabe, TrafficSlotEintrag } from "../types";
import {
  darfHeute,
  dauer,
  erlaubteStunden,
  gewichtBalken,
  heuteIso,
  kennzahlen,
  minuten,
  nachKunde,
  problem,
  tageszeitText,
  uhrzeit,
  WERBE_FEHLER,
  werbeStatus,
  zeitraumText,
  type WerbeStatus,
} from "../werbung";

/** Fehlertext: erst die Codes der Werbeplanung, sonst die allgemeine Übersetzung. */
function fehlerText(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  try {
    const code = (JSON.parse(raw.replace(/^\d{3}\s*/, "")) as { error?: string }).error;
    if (code && WERBE_FEHLER[code]) return WERBE_FEHLER[code]!;
  } catch {
    // kein JSON – allgemeine Übersetzung
  }
  return errorText(err);
}

const STATUS: Record<WerbeStatus, { label: string; tone: "success" | "info" | "neutral" }> = {
  laeuft: { label: "läuft", tone: "success" },
  geplant: { label: "geplant", tone: "info" },
  abgelaufen: { label: "abgelaufen", tone: "neutral" },
};

/** Welche Uhren des Kanals einen Werbeblock haben – aus Standard-Uhr und Sendeplan. */
type UhrenLage =
  | { stand: "laedt" }
  | { stand: "unbekannt" }
  | { stand: "ok"; uhren: Array<{ name: string; wo: string }> }
  | { stand: "fehlt"; standard: string | null; uhrenMitWerbung: number };

// ---------------------------------------------------------------------------

export function WerbungPage(): JSX.Element {
  const { user } = useAuth();
  // Die Routen verlangen „schedule.write“ UND die Rolle Radioleitung (siehe
  // apps/api/src/routes/schedules.ts). Wer nur das Recht hat, sieht die Liste.
  const darfPflegen = !!user && can(user.role, "schedule.write") && (user.role === "tenant_admin" || user.role === "super_admin");
  const darfNachweis = !!user && can(user.role, "reports.read");

  const [channels, setChannels] = useState<Channel[]>([]);
  const [slug, setSlug] = useState("");
  const [slots, setSlots] = useState<TrafficSlotEintrag[] | null>(null);
  const [spots, setSpots] = useState<LibraryItem[] | null>(null);
  const [uhren, setUhren] = useState<UhrenLage>({ stand: "laedt" });
  const [editing, setEditing] = useState<{ id: string | null; wert: Formular } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const heute = heuteIso();

  useEffect(() => {
    api.listChannels().then((c) => {
      setChannels(c);
      setSlug((s) => s || c[0]?.slug || "");
      if (c.length === 0) setSlots([]);
    }).catch((err) => setError(fehlerText(err)));
    api.listLibrary({ category: "ad", sort: "titel", limit: 200 }).then(setSpots).catch(() => setSpots([]));
  }, []);

  const load = useCallback(async () => {
    if (!slug) return;
    try {
      setSlots(await werbungApi.liste(slug));
    } catch (err) {
      setError(fehlerText(err));
      setSlots([]);
    }
  }, [slug]);

  useEffect(() => {
    setSlots(null);
    setEditing(null);
    setNotice(null);
    void load();
  }, [load]);

  // Hat der Kanal eine Uhr mit Werbeblock? Standard-Uhr (aus dem Sendeablauf)
  // plus alle Uhren, die im Sendeplan des Kanals eingetragen sind.
  useEffect(() => {
    if (!slug) return;
    let aktiv = true;
    setUhren({ stand: "laedt" });
    void (async () => {
      try {
        const [clocks, plan, schedules] = await Promise.all([api.listClocks(), api.getPlan(slug, 1), api.listSchedules(slug)]);
        const details = await Promise.all(schedules.map((s) => api.getSchedule(s.id).catch(() => null)));
        const mitWerbung = new Set(clocks.filter((c) => c.slots.some((s) => s.kind === "ad_break")).map((c) => c.id));
        const name = (id: string): string => clocks.find((c) => c.id === id)?.name ?? "Stundenuhr";
        const treffer = new Map<string, string>();
        const std = plan.default_clock?.id ?? null;
        if (std && mitWerbung.has(std)) treffer.set(std, "Standard-Uhr des Kanals");
        for (const d of details) {
          for (const b of d?.blocks ?? []) {
            if (b.kind === "clock" && b.clock_id && mitWerbung.has(b.clock_id) && !treffer.has(b.clock_id)) {
              treffer.set(b.clock_id, `im Sendeplan „${d!.name}“`);
            }
          }
        }
        if (!aktiv) return;
        setUhren(treffer.size > 0
          ? { stand: "ok", uhren: [...treffer].map(([id, wo]) => ({ name: name(id), wo })) }
          : { stand: "fehlt", standard: plan.default_clock?.name ?? null, uhrenMitWerbung: mitWerbung.size });
      } catch {
        if (aktiv) setUhren({ stand: "unbekannt" });
      }
    })();
    return () => { aktiv = false; };
  }, [slug]);

  const zahlen = useMemo(() => (slots ? kennzahlen(slots, heute) : null), [slots, heute]);
  const gruppen = useMemo(() => (slots ? nachKunde(slots) : []), [slots]);
  const maxGewicht = useMemo(() => Math.max(1, ...(slots ?? []).map((s) => s.weight)), [slots]);
  const kunden = useMemo(() => gruppen.map((g) => g.kunde), [gruppen]);
  const keineSpots = spots !== null && spots.length === 0;

  const neu = (kunde = ""): void => {
    setNotice(null);
    setError(null);
    setEditing({ id: null, wert: { ...LEER, advertiser: kunde } });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const bearbeiten = (s: TrafficSlotEintrag): void => {
    setNotice(null);
    setError(null);
    setEditing({ id: s.id, wert: ausEintrag(s) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const speichern = async (wert: Formular): Promise<void> => {
    if (!editing) return;
    setError(null);
    const eingabe = zuEingabe(wert, spots ?? []);
    try {
      if (editing.id) await werbungApi.aendern(editing.id, eingabe);
      else await werbungApi.anlegen(slug, eingabe);
      setNotice(editing.id ? "Buchung gespeichert. Ab der nächsten geplanten Stunde gilt der neue Stand." : `Buchung für „${eingabe.advertiser}“ angelegt.`);
      setEditing(null);
      await load();
    } catch (err) {
      setError(fehlerText(err));
    }
  };

  const loeschen = async (s: TrafficSlotEintrag): Promise<void> => {
    const text = `Buchung „${s.advertiser}${s.spot_title ? ` – ${s.spot_title}` : ""}“ löschen?\n\nDer Spot kommt danach in keinen Werbeblock mehr. Der Titel bleibt in der Mediathek, der Werbenachweis bleibt erhalten.`;
    if (!window.confirm(text)) return;
    setError(null);
    try {
      await werbungApi.loeschen(s.id);
      setNotice(`Buchung „${s.advertiser}“ gelöscht.`);
      if (editing?.id === s.id) setEditing(null);
      await load();
    } catch (err) {
      setError(fehlerText(err));
    }
  };

  return (
    <Page className="max-w-6xl">
      <PageHeader
        eyebrow="Programm"
        title="Werbeplanung"
        description={
          <>
            Welcher Kunde mit welchem Spot wann in die Werbeblöcke darf. Pro Werbeblock läuft jeder Kunde höchstens einmal;
            Spots, die heute seltener liefen, kommen zuerst – bei Gleichstand der mit dem höheren Gewicht.
          </>
        }
        actions={
          <>
            {channels.length > 1 && (
              <select value={slug} onChange={(e) => setSlug(e.target.value)} className="input !w-auto" aria-label="Kanal">
                {channels.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
              </select>
            )}
            {darfPflegen && !editing && !keineSpots && slug && (
              <Button variant="primary" onClick={() => neu()}><Icon name="plus" /> Neue Buchung</Button>
            )}
          </>
        }
      />

      <div className="space-y-6">
        {error && <Callout tone="danger">{error}</Callout>}
        {notice && <Callout tone="success">{notice}</Callout>}
        {!darfPflegen && (
          <Callout tone="info">Buchungen anlegen und ändern darf die Radioleitung. Du siehst hier, was gebucht ist.</Callout>
        )}

        <SoKommtWerbungAufSendung lage={uhren} />

        {/* Kennzahlen */}
        {zahlen ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatTile label="Aktive Spots" value={zahlen.aktiv} tone="brand" hint="im Zeitraum und mit Audio" />
            <StatTile label="Kunden" value={zahlen.kunden} hint="mit laufender oder geplanter Buchung" />
            <StatTile label="Heute möglich" value={zahlen.heute} tone={zahlen.heute > 0 ? "success" : "neutral"} hint="Spots, die heute in einen Block dürfen" />
            <StatTile label="Braucht Blick" value={zahlen.probleme} tone={zahlen.probleme > 0 ? "warning" : "neutral"} hint="ohne Audio oder abgelaufen" />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[6.5rem]" />)}</div>
        )}

        {/* Formular */}
        {editing && spots && (
          <BuchungsFormular
            key={editing.id ?? "neu"}
            neu={editing.id === null}
            start={editing.wert}
            spots={spots}
            kunden={kunden}
            onSave={speichern}
            onCancel={() => setEditing(null)}
          />
        )}

        {/* Ohne Werbespots in der Mediathek geht nichts */}
        {keineSpots && (
          <EmptyState
            icon={<Icon name="music" />}
            title="Noch keine Werbespots in der Mediathek."
            description={
              <>
                Lade den Spot als Titel in die Mediathek und stelle dort die Kategorie auf <b>„Werbung“</b>.
                Danach lässt er sich hier einem Kunden zuordnen.
              </>
            }
            action={<Link to="/library" className="btn btn-primary">Zur Mediathek</Link>}
          />
        )}

        {/* Buchungen nach Kunde */}
        {slots === null ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">{[0, 1].map((i) => <Skeleton key={i} className="h-48" />)}</div>
        ) : slots.length === 0 ? (
          !keineSpots && !editing && (
            <EmptyState
              icon={<Icon name="users" />}
              title="Noch keine Buchungen auf diesem Kanal."
              description="Eine Buchung verbindet einen Kunden mit einem Spot – auf Wunsch nur zu bestimmten Tageszeiten oder in einem Zeitraum."
              action={darfPflegen ? <Button variant="primary" onClick={() => neu()}><Icon name="plus" /> Erste Buchung anlegen</Button> : undefined}
            />
          )
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {gruppen.map((g) => (
              <KundenKarte
                key={g.kunde.toLowerCase()}
                kunde={g.kunde}
                slots={g.slots}
                heute={heute}
                maxGewicht={maxGewicht}
                darfPflegen={darfPflegen && !keineSpots}
                aktivId={editing?.id ?? null}
                onNeu={() => neu(g.kunde)}
                onEdit={bearbeiten}
                onDelete={(s) => void loeschen(s)}
              />
            ))}
          </div>
        )}

        {/* Nachweis */}
        <Card
          title="Werbenachweis"
          description="Was tatsächlich gesendet wurde – jeder Spot mit Zeit und Hörerzahl, als CSV für eure Kunden."
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="max-w-2xl text-sm text-secondary">
              Die Buchungen hier sagen, was laufen <i>darf</i>. Was wirklich lief, steht im Werbenachweis unter den Meldelisten
              (Monat und Kanal wählen, dann „Werbenachweis“).
            </p>
            {darfNachweis ? (
              <Link to="/reports" className="btn btn-secondary">Zum Werbenachweis</Link>
            ) : (
              <span className="text-xs text-muted">Den Nachweis sieht, wer Meldelisten lesen darf.</span>
            )}
          </div>
        </Card>
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Hilfe: wie Werbung auf Sendung kommt

function SoKommtWerbungAufSendung({ lage }: { lage: UhrenLage }): JSX.Element {
  const zustand =
    lage.stand === "ok" ? (
      <span className="flex flex-wrap items-center gap-1.5">
        <Badge tone="success" dot>Werbeblock vorhanden</Badge>
        {lage.uhren.map((u) => <span key={u.name + u.wo} className="text-xs">„{u.name}“ ({u.wo})</span>)}
      </span>
    ) : lage.stand === "fehlt" ? (
      <span className="flex flex-wrap items-center gap-1.5">
        <Badge tone="warning" dot>Kein Werbeblock</Badge>
        <span className="text-xs">
          {lage.standard
            ? `Die Standard-Uhr „${lage.standard}“ hat keinen Platz „Werbeblock“ – gebuchte Spots laufen so nicht.`
            : lage.uhrenMitWerbung > 0
              ? "Eine Uhr mit Werbeblock gibt es, sie gilt aber auf diesem Kanal nicht (weder Standard-Uhr noch im Sendeplan)."
              : "Dieser Kanal hat keine Stundenuhr mit Werbeblock – gebuchte Spots laufen so nicht."}
        </span>
      </span>
    ) : lage.stand === "laedt" ? (
      <span className="text-xs opacity-70">Prüfe Stundenuhren …</span>
    ) : null;

  return (
    <Callout tone={lage.stand === "fehlt" ? "warning" : "info"} title="So kommt Werbung auf Sendung">
      <ol className="mt-1 list-decimal space-y-0.5 pl-5">
        <li>Spot in die <Link to="/library" className="font-medium underline underline-offset-2">Mediathek</Link> laden, Kategorie „Werbung“.</li>
        <li>Hier eine Buchung anlegen: Kunde, Spot, Gewicht, bei Bedarf Tageszeit und Zeitraum.</li>
        <li>
          In der <Link to="/clocks" className="font-medium underline underline-offset-2">Stundenuhr</Link> des Kanals einen Platz „Werbeblock“ setzen –
          dort holt die Automatik die Spots ab.
        </li>
      </ol>
      {zustand && <div className="mt-2">{zustand}</div>}
    </Callout>
  );
}

// ---------------------------------------------------------------------------
// Karte je Kunde

function KundenKarte({ kunde, slots, heute, maxGewicht, darfPflegen, aktivId, onNeu, onEdit, onDelete }: {
  kunde: string;
  slots: TrafficSlotEintrag[];
  heute: string;
  maxGewicht: number;
  darfPflegen: boolean;
  aktivId: string | null;
  onNeu: () => void;
  onEdit: (s: TrafficSlotEintrag) => void;
  onDelete: (s: TrafficSlotEintrag) => void;
}): JSX.Element {
  // „laufend“ nur mit Spot – eine Buchung ohne Audio läuft nie, auch im Zeitraum.
  const laufend = slots.filter((s) => werbeStatus(s, heute) === "laeuft" && s.spot_library_id !== null).length;
  return (
    <section className="card flex min-w-0 flex-col">
      <div className="card-header">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-primary/10 text-xs font-semibold text-brand-primary" aria-hidden="true">
            {kunde.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0">
            <h2 className="card-title truncate">{kunde}</h2>
            <p className="text-xs text-muted">
              {slots.length === 1 ? "1 Spot" : `${slots.length} Spots`}{laufend > 0 ? ` · ${laufend} laufend` : ""}
            </p>
          </div>
        </div>
        {darfPflegen && (
          <Button size="sm" variant="ghost" onClick={onNeu} title={`Weiteren Spot für ${kunde} buchen`}><Icon name="plus" className="h-3.5 w-3.5" /> Spot</Button>
        )}
      </div>
      <ul className="divide-y divide-[var(--border)]">
        {slots.map((s) => (
          <SpotZeile key={s.id} s={s} heute={heute} maxGewicht={maxGewicht} darfPflegen={darfPflegen} aktiv={aktivId === s.id} onEdit={() => onEdit(s)} onDelete={() => onDelete(s)} />
        ))}
      </ul>
    </section>
  );
}

function SpotZeile({ s, heute, maxGewicht, darfPflegen, aktiv, onEdit, onDelete }: {
  s: TrafficSlotEintrag;
  heute: string;
  maxGewicht: number;
  darfPflegen: boolean;
  aktiv: boolean;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const status = werbeStatus(s, heute);
  const warnung = problem(s, heute);
  return (
    <li className={`space-y-3 px-5 py-4 ${aktiv ? "bg-brand-primary/[0.04]" : ""} ${status === "abgelaufen" ? "opacity-75" : ""}`}>
      {/* Spot: Titel, Dauer, Vorhören */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-primary" title={s.spot_title ?? undefined}>
              {s.spot_title ?? <span className="italic text-muted">kein Spot gewählt</span>}
            </span>
            <span className="flex-shrink-0 text-xs tabular-nums text-muted">{dauer(s.length_ms)}</span>
          </div>
          {s.notes && <p className="mt-0.5 line-clamp-2 text-xs text-muted">{s.notes}</p>}
        </div>
        {s.spot_library_id && (
          <audio controls preload="none" src={api.streamUrl(s.spot_library_id)} className="h-8 w-full min-w-0 sm:w-[190px] dark:[color-scheme:dark]" aria-label={`Vorhören: ${s.spot_title ?? "Spot"}`} />
        )}
      </div>

      {/* Tageszeit als Leiste 0–24 Uhr */}
      <Tagesleiste s={s} />

      {/* Gewicht, Zeitraum, Aktionen */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-secondary">
        <GewichtBalken weight={s.weight} maxWeight={maxGewicht} />
        <span className="flex items-center gap-1.5">
          <Badge tone={STATUS[status].tone} dot={status === "laeuft"}>{STATUS[status].label}</Badge>
          <span className="tabular-nums">{zeitraumText(s)}</span>
        </span>
        {warnung && status !== "abgelaufen" && <Badge tone="warning">{warnung}</Badge>}
        {darfHeute(s, heute) && <span className="text-emerald-700 dark:text-emerald-400">heute möglich</span>}
        {darfPflegen && (
          <span className="ml-auto flex items-center gap-1">
            <Button size="sm" variant="secondary" onClick={onEdit}>Bearbeiten</Button>
            <Button size="sm" variant="ghost" className="text-red-600 dark:text-red-400" onClick={onDelete} aria-label="Buchung löschen">
              <Icon name="trash" className="h-3.5 w-3.5" />
            </Button>
          </span>
        )}
      </div>
    </li>
  );
}

/** Tageszeit als Balken über 24 Stunden; die Stunden, in denen der Spot laufen darf, sind gefüllt. */
function Tagesleiste({ s }: { s: Pick<TrafficSlotEintrag, "daypart_start" | "daypart_end"> }): JSX.Element {
  const von = minuten(s.daypart_start) ?? 0;
  const bis = minuten(s.daypart_end) ?? 24 * 60;
  const stunden = erlaubteStunden(s).length;
  const links = (von / (24 * 60)) * 100;
  const breite = Math.max(0, ((bis - von) / (24 * 60)) * 100);
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
        <span className="font-medium text-secondary">{tageszeitText(s)}</span>
        <span className="text-muted">{stunden === 24 ? "alle Stunden" : `${stunden} ${stunden === 1 ? "Stunde" : "Stunden"}`}</span>
      </div>
      <div className="relative h-2.5 overflow-hidden rounded-full" style={{ background: "var(--bg-sunken)", boxShadow: "inset 0 0 0 1px var(--border)" }}
        role="img" aria-label={`Tageszeit: ${tageszeitText(s)}`}>
        {breite > 0 && <div className="absolute inset-y-0 rounded-full bg-brand-primary/80" style={{ left: `${links}%`, width: `${breite}%` }} />}
        {[6, 12, 18].map((h) => (
          <span key={h} className="absolute inset-y-0 w-px" style={{ left: `${(h / 24) * 100}%`, background: "var(--border-strong)", opacity: 0.6 }} aria-hidden="true" />
        ))}
      </div>
      <div className="relative mt-0.5 h-3 text-[0.65rem] tabular-nums text-muted" aria-hidden="true">
        {[0, 6, 12, 18, 24].map((h) => (
          <span key={h} className="absolute -translate-x-1/2 first:translate-x-0 last:-translate-x-full" style={{ left: `${(h / 24) * 100}%` }}>{h}</span>
        ))}
      </div>
    </div>
  );
}

function GewichtBalken({ weight, maxWeight }: { weight: number; maxWeight: number }): JSX.Element {
  const voll = gewichtBalken(weight, maxWeight);
  return (
    <span className="inline-flex items-center gap-1.5" title={`Gewicht ${weight} – höheres Gewicht heißt: öfter im Werbeblock`}>
      <span className="inline-flex h-3.5 items-end gap-[2px]" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((i) => (
          <span key={i} className={`w-[4px] rounded-[1px] ${i <= voll ? "bg-brand-primary" : "bg-[var(--border-strong)] opacity-50"}`} style={{ height: `${30 + i * 14}%` }} />
        ))}
      </span>
      <span className="tabular-nums">Gewicht {weight}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Formular

interface Formular {
  advertiser: string;
  spot_library_id: string;
  weight: number;
  ganztags: boolean;
  daypart_start: string;
  daypart_end: string;
  starts_at: string;
  ends_at: string;
  notes: string;
  /** Anzeige, falls der Spot nicht (mehr) unter den Werbespots der Mediathek steht. */
  spot_title_alt: string | null;
  length_ms_alt: number;
}

const LEER: Formular = {
  advertiser: "",
  spot_library_id: "",
  weight: 1,
  ganztags: true,
  daypart_start: "06:00",
  daypart_end: "20:00",
  starts_at: "",
  ends_at: "",
  notes: "",
  spot_title_alt: null,
  length_ms_alt: 30_000,
};

function ausEintrag(s: TrafficSlotEintrag): Formular {
  const ganztags = !s.daypart_start && !s.daypart_end;
  return {
    advertiser: s.advertiser,
    spot_library_id: s.spot_library_id ?? "",
    weight: s.weight,
    ganztags,
    daypart_start: ganztags ? LEER.daypart_start : uhrzeit(s.daypart_start),
    daypart_end: ganztags ? LEER.daypart_end : uhrzeit(s.daypart_end),
    starts_at: s.starts_at ?? "",
    ends_at: s.ends_at ?? "",
    notes: s.notes ?? "",
    spot_title_alt: s.spot_title,
    length_ms_alt: s.length_ms,
  };
}

/** Formular → API-Rumpf. Leere Felder werden null (leert sie beim Bearbeiten). */
function zuEingabe(f: Formular, spots: LibraryItem[]): TrafficSlotEingabe {
  const spot = spots.find((x) => x.id === f.spot_library_id);
  return {
    advertiser: f.advertiser.trim(),
    spot_library_id: f.spot_library_id || null,
    // Länge folgt dem Spot; nur wenn die Mediathek keine Dauer kennt, bleibt der alte Wert.
    length_ms: Math.max(500, Math.min(600_000, spot?.duration_ms ?? f.length_ms_alt)),
    weight: f.weight,
    daypart_start: f.ganztags ? null : f.daypart_start || null,
    daypart_end: f.ganztags ? null : f.daypart_end || null,
    starts_at: f.starts_at || null,
    ends_at: f.ends_at || null,
    notes: f.notes.trim() || null,
  };
}

/** Was vor dem Absenden schon feststeht – dieselben Regeln prüft die API noch einmal. */
function formularFehler(f: Formular): string | null {
  if (f.advertiser.trim().length < 2) return "Kunde: mindestens zwei Zeichen.";
  if (!f.spot_library_id) return "Bitte einen Spot wählen.";
  if (!Number.isInteger(f.weight) || f.weight < 1 || f.weight > 1000) return "Gewicht: ganze Zahl von 1 bis 1000.";
  if (!f.ganztags) {
    if (!f.daypart_start && !f.daypart_end) return "Tageszeit: mindestens Beginn oder Ende angeben – oder „ganztägig“ wählen.";
    if (f.daypart_start && f.daypart_end && f.daypart_end <= f.daypart_start) return WERBE_FEHLER.daypart_invalid!;
  }
  if (f.starts_at && f.ends_at && f.ends_at < f.starts_at) return WERBE_FEHLER.period_invalid!;
  return null;
}

function BuchungsFormular({ neu, start, spots, kunden, onSave, onCancel }: {
  neu: boolean;
  start: Formular;
  spots: LibraryItem[];
  kunden: string[];
  onSave: (f: Formular) => Promise<void>;
  onCancel: () => void;
}): JSX.Element {
  const [f, setF] = useState<Formular>(start);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<Formular>): void => setF((alt) => ({ ...alt, ...patch }));
  const fehler = formularFehler(f);
  const spot = spots.find((x) => x.id === f.spot_library_id);
  const fremd = f.spot_library_id && !spot; // Spot steht nicht (mehr) als Werbung in der Mediathek
  const vorschau = { daypart_start: f.ganztags ? null : f.daypart_start || null, daypart_end: f.ganztags ? null : f.daypart_end || null };
  const stunden = erlaubteStunden(vorschau).length;

  const absenden = async (): Promise<void> => {
    if (fehler) return;
    setBusy(true);
    try {
      await onSave(f);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={neu ? "Neue Buchung" : "Buchung bearbeiten"} description="Kunde und Spot sind Pflicht; alles andere ist optional.">
      <form
        className="grid grid-cols-1 gap-5 md:grid-cols-2"
        onSubmit={(e) => { e.preventDefault(); void absenden(); }}
      >
        <div>
          <label className="field-label" htmlFor="w-kunde">Kunde</label>
          <input id="w-kunde" className="input" list="w-kunden" value={f.advertiser} maxLength={120} autoFocus={neu && !f.advertiser}
            onChange={(e) => set({ advertiser: e.target.value })} placeholder="z. B. Bäckerei Schmitz" />
          <datalist id="w-kunden">{kunden.map((k) => <option key={k} value={k} />)}</datalist>
          <p className="field-hint">Gleicher Name = gleicher Kunde: pro Werbeblock läuft er höchstens einmal.</p>
        </div>

        <div>
          <label className="field-label" htmlFor="w-spot">Spot</label>
          <select id="w-spot" className="input" value={f.spot_library_id} onChange={(e) => set({ spot_library_id: e.target.value })}>
            <option value="">Spot wählen …</option>
            {fremd && <option value={f.spot_library_id}>{f.spot_title_alt ?? "bisheriger Spot"} (nicht als Werbung markiert)</option>}
            {spots.map((x) => (
              <option key={x.id} value={x.id}>{x.title}{x.artist ? ` – ${x.artist}` : ""} ({dauer(x.duration_ms)})</option>
            ))}
          </select>
          {f.spot_library_id ? (
            <div className="mt-2 flex items-center gap-2">
              <audio controls preload="none" src={api.streamUrl(f.spot_library_id)} className="h-8 min-w-0 flex-1 dark:[color-scheme:dark]" aria-label="Spot vorhören" />
              <span className="text-xs tabular-nums text-muted">{dauer(spot?.duration_ms ?? f.length_ms_alt)}</span>
            </div>
          ) : (
            <p className="field-hint">Zur Auswahl stehen Titel der Kategorie „Werbung“. <Link to="/library" className="underline underline-offset-2">Mediathek</Link></p>
          )}
        </div>

        <div>
          <span className="field-label">Gewicht</span>
          <div className="flex flex-wrap items-center gap-2">
            {[1, 2, 3, 5].map((g) => (
              <button key={g} type="button" onClick={() => set({ weight: g })}
                className={`btn btn-sm ${f.weight === g ? "btn-primary" : "btn-secondary"} !w-9 tabular-nums`} aria-pressed={f.weight === g}>{g}</button>
            ))}
            <input type="number" min={1} max={1000} step={1} className="input !h-[1.875rem] !w-20 tabular-nums" aria-label="Gewicht als Zahl"
              value={Number.isFinite(f.weight) ? f.weight : ""} onChange={(e) => set({ weight: Math.round(Number(e.target.value)) })} />
          </div>
          <p className="field-hint">Gewicht 2 läuft etwa doppelt so oft wie Gewicht 1 – solange beide in dieselben Blöcke dürfen.</p>
        </div>

        <div>
          <span className="field-label">Tageszeit</span>
          <div className="tabbar" role="radiogroup" aria-label="Tageszeit">
            <button type="button" role="radio" aria-checked={f.ganztags} className={`tab ${f.ganztags ? "tab-active" : ""}`} onClick={() => set({ ganztags: true })}>ganztägig</button>
            <button type="button" role="radio" aria-checked={!f.ganztags} className={`tab ${!f.ganztags ? "tab-active" : ""}`} onClick={() => set({ ganztags: false })}>nur von … bis</button>
          </div>
          {!f.ganztags && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input type="time" step={3600} className="input !w-auto" aria-label="Tageszeit von" value={f.daypart_start} onChange={(e) => set({ daypart_start: e.target.value })} />
              <span className="text-sm text-muted">bis</span>
              <input type="time" step={3600} className="input !w-auto" aria-label="Tageszeit bis" value={f.daypart_end} onChange={(e) => set({ daypart_end: e.target.value })} />
            </div>
          )}
          <div className="mt-3"><Tagesleiste s={vorschau} /></div>
          <p className="field-hint">
            Gezählt wird der Beginn der Stunde: 06:00–10:00 heißt die Stunden ab 6, 7, 8 und 9 Uhr{stunden === 0 && !f.ganztags ? " – so ist keine volle Stunde dabei" : ""}.
            Leeres „bis“ heißt bis Tagesende.
          </p>
        </div>

        <div>
          <span className="field-label">Zeitraum</span>
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" className="input !w-auto" aria-label="Zeitraum von" value={f.starts_at} onChange={(e) => set({ starts_at: e.target.value })} />
            <span className="text-sm text-muted">bis</span>
            <input type="date" className="input !w-auto" aria-label="Zeitraum bis" value={f.ends_at} min={f.starts_at || undefined} onChange={(e) => set({ ends_at: e.target.value })} />
          </div>
          <p className="field-hint">Leer lassen für unbefristet. Beide Tage zählen mit.</p>
        </div>

        <div>
          <label className="field-label" htmlFor="w-notiz">Notiz</label>
          <textarea id="w-notiz" rows={3} maxLength={500} className="input" value={f.notes} onChange={(e) => set({ notes: e.target.value })}
            placeholder="z. B. Auftragsnummer, Ansprechpartner, Sonderwünsche" />
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-default pt-4 md:col-span-2">
          <button type="submit" className="btn btn-primary" disabled={!!fehler || busy}>{neu ? "Buchung anlegen" : "Speichern"}</button>
          <Button variant="ghost" onClick={onCancel}>Abbrechen</Button>
          {fehler && <span className="text-xs text-muted">{fehler}</span>}
        </div>
      </form>
    </Card>
  );
}
